/** @jsxImportSource @emotion/react */
import React, { useState } from 'react';
import { Button, Popover } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { DateRangeType, DateType } from 'react-tailwindcss-datepicker';
import { CommonText } from 'ui/theme/Color';
import CustomDatepicker from 'components/inputs/CustomDatepicker';
import { IconButton } from 'components/buttons/IconSVG';
import { findDailyReportList } from 'apis/setup/SetupDailyReport';
import useSessionStore from 'stores/useSessionStore';

interface Props {
  elmPrjId?: string;
  excpId?: string;
  value?: string; // 현재 기준일자 (YYYYMMDD)
  onSelectDate: (dateStr: string) => void; // 선택한 날짜
}

/**
 * @desc 프로젝트 셋업 일보 조회 > 셋업일보 등록/수정 화면 > 셋업일보 조회용 달력 버튼
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2026.09.09   jshwang12      신규 생성
 * 2026.09.17   jshwang12      <> 버튼, 일보이동달력 수정: 임시저장 상태인 경우 작성자에 한해 노출/이동 O, 수정상태인 경우에만 미노출
 */
const SetupDailyReportCalendarButton = ({ elmPrjId, excpId, value, onSelectDate }: Props) => {
  const { userId } = useSessionStore();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [disabledDates, setDisabledDates] = useState<DateRangeType[]>([]);
  // 작업일보 목록에서 가장 오래된/최신 기준일자로 조회·달력 범위를 잡는다.
  const [bounds, setBounds] = useState<{ start: Dayjs; end: Dayjs } | null>(null);

  const ONE_DAY_MS = 24 * 60 * 60 * 1000;

  /**
   * 선택 가능한(작업일보가 존재하는) 날짜 강조
   */
  const availableDayCss = {
    '.text-gray-700 .transition-all.ease-out > div .flex > .w-full > div:nth-child(2) > div:nth-child(2) > button:not(.text-gray-400):not(.text-gray-300):not(.line-through):not(.bg-blue-500)':
      {
        color: CommonText.Confirmed,
        fontWeight: 700,
      },
  };

  const parseCrtnDt = (v?: string): Dayjs | null => {
    if (!v) return null;
    const digits = String(v).replace(/[^0-9]/g, '');
    if (digits.length === 8) {
      return dayjs(digits, 'YYYYMMDD');
    }
    const parsed = dayjs(v);
    return parsed.isValid() ? parsed : null;
  };

  /**
   * 작업일보가 존재하는 날짜를 뺀 나머지 구간을 계산
   */
  const buildDisabledRanges = (
    availableDates: Dayjs[],
    boundStart: Dayjs,
    boundEnd: Dayjs
  ): DateRangeType[] => {
    const sortedTimes = Array.from(
      new Set(availableDates.map((d) => d.startOf('day').valueOf()))
    ).sort((a, b) => a - b);

    const ranges: DateRangeType[] = [];
    let cursor = boundStart.startOf('day').valueOf();
    const endTime = boundEnd.startOf('day').valueOf();

    sortedTimes.forEach((t) => {
      if (t < cursor || t > endTime) return;
      if (t > cursor) {
        ranges.push({
          startDate: dayjs(cursor).toDate(),
          endDate: dayjs(t - ONE_DAY_MS).toDate(),
        });
      }
      cursor = t + ONE_DAY_MS;
    });

    if (cursor <= endTime) {
      ranges.push({ startDate: dayjs(cursor).toDate(), endDate: dayjs(endTime).toDate() });
    }

    return ranges;
  };

  const open = Boolean(anchorEl);
  const parsedValue = parseCrtnDt(value);

  const loadDisabledDates = async () => {
    if (!elmPrjId || !excpId) {
      setBounds(null);
      setDisabledDates([]);
      return;
    }
    const result = await findDailyReportList({
      elmPrjId,
      excpId,
      dailyReportYn: 'Y',
    });
    const dates = (result || [])
      // 임시저장 상태인 일보는 작성자 본인만 선택 가능
      .filter((item) => !(item.wkStatCd === 'SAVE' && item.dataInsUserId !== userId))
      .map((item) => parseCrtnDt(item.crtnDt))
      .filter((d): d is Dayjs => !!d)
      .sort((a, b) => a.valueOf() - b.valueOf());

    if (dates.length < 1) {
      setBounds(null);
      setDisabledDates([]);
      return;
    }

    const boundStart = dates[0];
    const boundEnd = dates[dates.length - 1];
    setBounds({ start: boundStart, end: boundEnd });
    setDisabledDates(buildDisabledRanges(dates, boundStart, boundEnd));
  };

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    loadDisabledDates();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChange = (newValue: DateType) => {
    if (!newValue) return;
    const parsed = dayjs(String(newValue));
    if (!parsed.isValid()) return;
    onSelectDate(parsed.format('YYYY-MM-DD'));
    handleClose();
  };

  return (
    <>
      <Button
        css={IconButton.button}
        className="date iconOnly"
        onClick={handleOpen}
        disableRipple
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              marginTop: '4px',
              overflow: 'visible',
              maxHeight: 'none !important',
            },
          },
        }}
      >
        <div css={availableDayCss} style={{ width: '293px', height: '316px' }}>
          <CustomDatepicker
            isAlwaysOepn
            isRange={false}
            readOnly
            value={parsedValue?.toDate() ?? null}
            startFrom={parsedValue?.toDate() ?? new Date()}
            minDate={bounds?.start.toDate() ?? null}
            maxDate={bounds?.end.toDate()}
            disabledDates={disabledDates}
            onChange={handleChange}
          />
        </div>
      </Popover>
    </>
  );
};

export default SetupDailyReportCalendarButton;
